<template>
  <div class="build-assembly-container build-assembly-page--glass glass-tab-page">
    <div class="build-assembly-header-bar">
      <BrucdcumHeader
        :breadcrumb="['Inventory', 'Build Assembly']"
        :show-back-button="true"
        :back-fallback="backFallbackRoute"
      />
    </div>

    <div class="build-assembly-toolbar">
      <SecondaryButton label="Search" icon="bi bi-search" @click="onSearchClick" />
      <PrimaryButton label="Add New Inventory Assembly" icon="bi bi-plus-lg" @click="addNewAssembly" />
    </div>

    <form class="build-assembly-form" @submit.prevent>
      <div class="glass-tab-view glass-tab-view--flush build-assembly-glass-shell">
        <div class="glass-tab-view__content build-assembly-tab-content">
          <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows build-assembly-section build-assembly-section--details">
            <h3 class="glass-tab-panel__title">
              <i class="bi bi-tools glass-tab-panel__title-icon" aria-hidden="true"></i>
              Build Assembly Details
            </h3>
            <div class="build-assembly-details-grid">
              <div class="build-assembly-form-group build-assembly-form-group--label-dropdown">
                <LabelDropdown
                  label="Assembly Item"
                  select-id="ba-assembly-item"
                  v-model="selectedAssemblyItem"
                  flex
                  wide
                  required
                  searchable
                  :options="assemblyDropdownOptions"
                  @change="handleAssemblyItemChange"
                />
              </div>
              <div class="build-assembly-form-group build-assembly-form-group--label-input">
                <LabelInputField
                  input-id="ba-qty-on-hand"
                  label="Quantity On Hand"
                  :model-value="String(assemblyOnHandQuantity)"
                  flex
                  disabled
                />
              </div>
              <div class="build-assembly-form-group build-assembly-form-group--label-input">
                <LabelInputField
                  input-id="ba-build-point"
                  label="Build Point"
                  :model-value="selectedAssembly && selectedAssembly.reorderPoint != null ? String(selectedAssembly.reorderPoint) : '0.00'"
                  flex
                  disabled
                />
              </div>
              <div class="build-assembly-form-group build-assembly-form-group--label-date">
                <LabelDateField
                  input-id="ba-build-date"
                  label="Date"
                  v-model="buildDatePicker"
                  flex
                />
              </div>
              <div class="build-assembly-form-group build-assembly-form-group--label-input">
                <LabelInputField
                  input-id="ba-ref-no"
                  label="Ref No."
                  v-model="refNo"
                  flex
                />
              </div>
            </div>
          </div>

          <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows build-assembly-section build-assembly-section--table">
            <h3 class="glass-tab-panel__title">
              <i class="bi bi-boxes glass-tab-panel__title-icon" aria-hidden="true"></i>
              Assembly Item Components
            </h3>
            <div class="consultant-glass-table-section build-assembly-table-section">
              <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
                <div class="consultant-glass-table-scroll-x">
                  <table class="consultant-glass-table build-assembly-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Quantity On Hand</th>
                        <th>Quantity Needed</th>
                        <th>Average Cost (USD)</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!selectedAssembly || !selectedAssembly.items || !selectedAssembly.items.length">
                        <td colspan="6" class="build-assembly-table-empty">No records to show</td>
                      </tr>
                      <tr
                        v-for="(item, idx) in (selectedAssembly && selectedAssembly.items ? selectedAssembly.items : [])"
                        :key="item.id"
                        class="build-assembly-table-row consultant-glass-table-row"
                        :style="{ '--ba-row-delay': `${Math.min(idx * 0.03, 0.45)}s` }"
                      >
                        <td>{{ item.name }}</td>
                        <td>{{ item.description }}</td>
                        <td>{{ getOnHandQuantityForComponent(item) }}</td>
                        <td>{{ item.quantity }}</td>
                        <td>{{ item.avgCost }}</td>
                        <td>{{ item.quantity && item.avgCost ? (item.quantity * item.avgCost).toFixed(2) : '' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="build-assembly-table-helper-text">Components required for this assembly will appear here.</div>
          </div>

          <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows build-assembly-section build-assembly-section--footer">
            <div class="build-assembly-footer-grid">
              <div class="build-assembly-form-group build-assembly-form-group--label-description">
                <LabelDescriptionField
                  input-id="ba-comments"
                  label="Write comments for this Build Assembly."
                  v-model="comments"
                  :rows="2"
                  placeholder="Add any notes or comments..."
                  flex
                />
              </div>
              <div class="build-assembly-summary-panel">
                <LabelInputField
                  input-id="ba-max-build"
                  label="Maximum number you can build from the quantity on hand"
                  :model-value="String(maxBuildQuantity)"
                  flex
                  disabled
                />
                <LabelInputField
                  input-id="ba-qty-to-build"
                  label="Quantity to Build"
                  v-model="quantityToBuildInput"
                  type="number"
                  flex
                  :has-error="quantityToBuildError"
                  @input="validateQuantityToBuild"
                >
                  <div v-if="quantityToBuildError" class="build-assembly-qty-error">
                    Quantity to Build cannot exceed maximum buildable quantity ({{ maxBuildQuantity }}).
                  </div>
                </LabelInputField>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="build-assembly-form-actions glass-tab-view__actions">
        <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="onCancel" />
        <PrimaryButton label="Build and Close" icon="bi bi-check-lg" @click="buildAssembly('close')" />
        <PrimaryButton label="Build and New" icon="bi bi-arrow-repeat" @click="buildAssembly('new')" />
      </div>
    </form>
  </div>
</template>

<script>
import '../styles/BuildAssembly.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

function formatDateForPicker(value) {
  if (!value) return '';
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${month}/${day}/${year}`;
  }
  return value;
}

function convertPickerToISO(value) {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    const [month, day, year] = value.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return '';
}

function todayIso() {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default {
  name: 'BuildAssembly',
  components: {
    BrucdcumHeader,
    LabelDropdown,
    LabelInputField,
    LabelDateField,
    LabelDescriptionField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      assemblyItems: [],
      selectedAssemblyItem: '',
      buildDate: todayIso(),
      refNo: '1',
      selectedAssembly: null,
      inventoryItems: [],
      quantityToBuild: 0,
      quantityToBuildError: false,
      comments: '',
    };
  },
  computed: {
    backFallbackRoute() {
      const companyId = this.$route.params.id;
      return companyId ? `/company/${companyId}/dashboard/inventory/center` : '';
    },
    assemblyDropdownOptions() {
      return [
        { value: '', label: 'Select Assembly Item' },
        ...this.assemblyItems.map((item) => ({ value: item.id, label: item.name })),
        { value: '__create_new__', label: 'Create new assembly item…' },
      ];
    },
    buildDatePicker: {
      get() {
        return formatDateForPicker(this.buildDate);
      },
      set(value) {
        this.buildDate = convertPickerToISO(value) || this.buildDate;
      },
    },
    quantityToBuildInput: {
      get() {
        return this.quantityToBuild === 0 || this.quantityToBuild ? String(this.quantityToBuild) : '';
      },
      set(value) {
        const parsed = Number(value);
        this.quantityToBuild = Number.isFinite(parsed) ? parsed : 0;
      },
    },
    assemblyOnHandQuantity() {
      if (!this.selectedAssembly) return 0;
      const item = this.inventoryItems.find(
        (i) =>
          i.name === this.selectedAssembly.name &&
          String(i.companyId) === String(this.selectedAssembly.companyId)
      );
      if (item) return Number(item.onHandQuantity) || 0;
      return Number(this.selectedAssembly.onHandQuantity) || 0;
    },
    maxBuildQuantity() {
      if (!this.selectedAssembly || !this.selectedAssembly.items || !this.selectedAssembly.items.length) return 0;
      const ratios = this.selectedAssembly.items.map((item) => {
        const onHand = this.getOnHandQuantityForComponent(item);
        const needed = Number(item.quantity) || 0;
        if (!needed) return Infinity;
        return Math.floor(Number(onHand) / needed);
      });
      if (!ratios.length) return 0;
      const min = Math.min(...ratios);
      return isFinite(min) && min >= 0 ? min : 0;
    },
  },
  mounted() {
    this.fetchAssemblies();
  },
  methods: {
    async fetchAssemblies() {
      const companyId = this.$route.params.id;
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/assemblies`);
        if (!response.ok) throw new Error('Failed to fetch assemblies');
        const data = await response.json();
        this.assemblyItems = Array.isArray(data) ? data : [];
        const inventoryRes = await fetch(`${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/items`);
        if (!inventoryRes.ok) throw new Error('Failed to fetch inventory items');
        const inventoryItems = await inventoryRes.json();
        this.inventoryItems = Array.isArray(inventoryItems) ? inventoryItems : [];

        // Keep selected assembly in sync after refresh
        if (this.selectedAssemblyItem) {
          this.selectedAssembly =
            this.assemblyItems.find((a) => String(a.id) === String(this.selectedAssemblyItem)) || null;
        }
      } catch (error) {
        this.assemblyItems = [];
        this.inventoryItems = [];
      }
    },
    handleAssemblyItemChange() {
      if (this.selectedAssemblyItem === '__create_new__') {
        this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/new-assembly`);
        return;
      }
      if (!this.selectedAssemblyItem) {
        this.selectedAssembly = null;
        this.quantityToBuild = 0;
        return;
      }
      this.selectedAssembly =
        this.assemblyItems.find((a) => String(a.id) === String(this.selectedAssemblyItem)) || null;
      this.quantityToBuild = 0;
      this.quantityToBuildError = false;
    },
    getOnHandQuantityForComponent(item) {
      if (!item) return 0;
      const byId = this.inventoryItems.find(
        (i) => item.inventoryItemId != null && String(i.id) === String(item.inventoryItemId)
      );
      if (byId) return Number(byId.onHandQuantity) || 0;

      // Fallback when inventoryItemId is missing or mismatched
      const byName = this.inventoryItems.find(
        (i) => item.name && String(i.name).trim().toLowerCase() === String(item.name).trim().toLowerCase()
      );
      return byName ? Number(byName.onHandQuantity) || 0 : 0;
    },
    addNewAssembly() {
      this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/new-assembly`);
    },
    onSearchClick() {
      this.$toast?.info?.('Search functionality coming soon!') || alert('Search functionality coming soon!');
    },
    onCancel() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push(this.backFallbackRoute);
      }
    },
    validateQuantityToBuild() {
      if (this.quantityToBuild > this.maxBuildQuantity) {
        this.quantityToBuild = this.maxBuildQuantity;
        this.quantityToBuildError = true;
      } else {
        this.quantityToBuildError = false;
      }
    },
    async buildAssembly(action) {
      if (!this.selectedAssembly || !this.selectedAssembly.id) {
        this.$toast?.error?.('Please select an assembly item.') || alert('Please select an assembly item.');
        return;
      }
      if (!this.quantityToBuild || this.quantityToBuild <= 0) {
        this.$toast?.error?.('Please enter a valid quantity to build.') || alert('Please enter a valid quantity to build.');
        return;
      }
      if (this.quantityToBuild > this.maxBuildQuantity) {
        this.quantityToBuildError = true;
        this.$toast?.error?.(`Quantity to build cannot exceed ${this.maxBuildQuantity}.`) ||
          alert(`Quantity to build cannot exceed ${this.maxBuildQuantity}.`);
        return;
      }
      try {
        const payload = {
          buildQty: this.quantityToBuild,
          date: this.buildDate,
          refNo: this.refNo,
          comments: this.comments || '',
        };
        const response = await fetch(`${process.env.VUE_APP_API_URL}/inventory/assemblies/${this.selectedAssembly.id}/build`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          let serverMessage = 'Failed to build assembly';
          try {
            const errorBody = await response.json();
            if (errorBody?.error) serverMessage = errorBody.error;
          } catch (_) { /* ignore */ }
          throw new Error(serverMessage);
        }
        this.$toast?.success?.('Assembly built successfully!') || alert('Assembly built successfully!');
        await this.fetchAssemblies();
        if (action === 'close') {
          this.$router.push({ name: 'InventoryItems', params: { id: this.$route.params.id } });
        } else {
          this.resetForm();
        }
      } catch (error) {
        this.$toast?.error?.(error.message || 'Failed to build assembly') || alert(error.message || 'Failed to build assembly');
      }
    },
    resetForm() {
      this.quantityToBuild = 0;
      this.quantityToBuildError = false;
      this.refNo = '1';
      this.buildDate = todayIso();
      this.comments = '';
      this.selectedAssemblyItem = '';
      this.selectedAssembly = null;
    },
  },
};
</script>
